import {
  Component,
  inject,
  signal,
  computed,
  ChangeDetectionStrategy,
  OnInit,
} from '@angular/core';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { TranslatePipe } from '@ngx-translate/core';
import { MatomoService } from '../../../root-services/matomo.service';
import { ConsentStorageService } from '../../../root-services/consent-storage.service';

/**
 * Dialog configuration constants
 */
export const COOKIE_PREFERENCES_DIALOG_CONFIG = {
  width: '600px',
  maxWidth: '90vw',
} as const;

/**
 * Cookie Categories for extensibility
 * Story 6.7 AC6: Scalable design for future cookie categories
 */
interface CookieCategory {
  id: string;
  titleKey: string;
  descriptionKey: string;
  toggleAriaKey: string;
  required: boolean;
  enabled: boolean;
}

/**
 * Cookie Preferences Dialog Component
 *
 * Provides a dedicated UI for managing cookie consent preferences
 * Designed to be extensible for future cookie categories
 *
 * Story 6.7: Cookie Preferences Management (Persistent Entry Point)
 * AC6-8: Scalable preferences UI with accessibility
 */
@Component({
  selector: 'app-cookie-preferences-dialog',
  standalone: true,
  imports: [
    MatDialogModule,
    MatButtonModule,
    MatSlideToggleModule,
    TranslatePipe,
  ],
  templateUrl: './cookie-preferences-dialog.component.html',
  styleUrl: './cookie-preferences-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CookiePreferencesDialogComponent implements OnInit {
  private readonly dialogRef = inject(
    MatDialogRef<CookiePreferencesDialogComponent>,
  );
  private readonly matomoService = inject(MatomoService);
  private readonly consentStorage = inject(ConsentStorageService);

  /**
   * Cookie categories configuration
   * AC6: Extensible list/config for multiple cookie categories
   * Note: Only Matomo analytics cookies shown - app uses localStorage for other data
   */
  categories = signal<CookieCategory[]>([
    {
      id: 'analytics',
      titleKey: 'CONSENT.PREFERENCES.ANALYTICS.TITLE',
      descriptionKey: 'CONSENT.PREFERENCES.ANALYTICS.DESCRIPTION',
      toggleAriaKey: 'CONSENT.PREFERENCES.ANALYTICS.TOGGLE_ARIA',
      required: false,
      enabled: false,
    },
  ]);

  /**
   * Track if there are unsaved changes
   *
   * Note: This computed calls getConsent() which now reads from the reactive
   * consent signal. The computed re-evaluates when categories() signal changes
   * or when the consent signal changes, which is sufficient for this dialog's
   * lifecycle since consent is modified through save() or externally.
   */
  hasChanges = computed(() => {
    const current = this.categories();
    const stored = this.consentStorage.getConsent();
    const analyticsCategory = current.find((cat) => cat.id === 'analytics');

    if (!analyticsCategory) return false;

    if (stored === 'accepted') {
      return !analyticsCategory.enabled;
    } else if (stored === 'denied') {
      return analyticsCategory.enabled;
    } else {
      return analyticsCategory.enabled; // Default is false, so any true is a change
    }
  });

  ngOnInit(): void {
    // Load consent state immediately on init
    // Consent state is independent of language/translation loading
    this.loadConsentState();
  }

  /**
   * Load current preference state from storage
   * AC10: Load current preference state from storage
   */
  private loadConsentState(): void {
    const storedConsent = this.consentStorage.getConsent();
    if (storedConsent === 'accepted') {
      this.updateCategoryState('analytics', true);
    } else if (storedConsent === 'denied') {
      this.updateCategoryState('analytics', false);
    }
  }

  /**
   * Update a cookie category's enabled state (pending)
   */
  private updateCategoryState(categoryId: string, enabled: boolean): void {
    this.categories.update((cats) =>
      cats.map((cat) => (cat.id === categoryId ? { ...cat, enabled } : cat)),
    );
  }

  /**
   * Handle category toggle change (updates local state only)
   * AC9: Changes are pending until user clicks Save
   * Extensible for future cookie categories beyond analytics
   */
  onCategoryToggleChange(categoryId: string, enabled: boolean): void {
    this.updateCategoryState(categoryId, enabled);
  }

  /**
   * Save preferences and apply changes
   * AC9: Apply preference when user clicks Save
   */
  save(): void {
    const analyticsCategory = this.categories().find(
      (cat) => cat.id === 'analytics',
    );

    if (analyticsCategory) {
      if (analyticsCategory.enabled) {
        this.consentStorage.setConsent('accepted');
        this.matomoService.giveConsent();
      } else {
        this.consentStorage.setConsent('denied');
        this.matomoService.revokeConsent();
      }
    }

    this.dialogRef.close();
  }

  /**
   * Get current analytics category state
   */
  getAnalyticsEnabled(): boolean {
    return (
      this.categories().find((cat) => cat.id === 'analytics')?.enabled ?? false
    );
  }

  /**
   * Close the dialog
   */
  close(): void {
    this.dialogRef.close();
  }
}
